import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Gift, Sparkles, RotateCcw } from 'lucide-react';
import { BUNDLES } from '../data/mock';
import { useProducts } from '../hooks/useProducts';

const steps = [
  { k: 'who', q: 'Who is it for?', opts: [
    { v: 'men', l: 'Him' },
    { v: 'women', l: 'Her' },
    { v: 'unisex', l: 'Anyone' },
  ]},
  { k: 'cat', q: 'What do they love most?', opts: [
    { v: 'skincare', l: 'Skincare & glow' },
    { v: 'grooming', l: 'Beauty & grooming' },
    { v: 'jewelry', l: 'Jewelry' },
    { v: 'selfcare', l: 'Self-care rituals' },
  ]},
  { k: 'budget', q: 'A gentle budget?', opts: [
    { v: 500, l: 'Under R 500' },
    { v: 900, l: 'Under R 900' },
    { v: 10000, l: 'Spoil them' },
  ]},
];

export default function GiftFinderPage() {
  const { products } = useProducts();
  const [step, setStep] = useState(0);
  const [ans, setAns] = useState({});

  const pick = (k, v) => {
    const next = { ...ans, [k]: v };
    setAns(next);
    if (step < steps.length - 1) setStep(step + 1);
    else setStep(steps.length);
  };
  const reset = () => { setAns({}); setStep(0); };

  const results = React.useMemo(() => {
    if (step < steps.length) return { products: [], bundles: [] };
    const genderMatch = (g) => !ans.who || g === ans.who || (ans.who === 'unisex');
    const prodMatches = products
      .filter((p) => genderMatch(p.gender) && (!ans.cat || p.category === ans.cat) && (!ans.budget || p.price <= ans.budget))
      .slice(0, 4);
    const bundleMatches = BUNDLES
      .filter((b) => (!ans.who || b.gender === ans.who || b.gender === 'unisex') && (!ans.budget || b.price <= ans.budget))
      .slice(0, 2);
    return { products: prodMatches, bundles: bundleMatches };
  }, [step, ans, products]);

  return (
    <div className="fade-in">
      <section className="bg-espresso text-ivory">
        <div className="mx-auto max-w-5xl px-5 py-20 text-center lg:px-8">
          <p className="eyebrow text-gold flex items-center justify-center gap-2"><Sparkles className="size-4" /> Gift Finder</p>
          <h1 className="mt-3 font-display text-5xl md:text-7xl">Find a gift they’ll quietly love.</h1>
          <p className="mx-auto mt-6 max-w-xl text-ivory/80">Three gentle questions. One thoughtfully-chosen edit — beauty, skincare, grooming or jewelry.</p>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 py-16 lg:px-8">
        {step < steps.length ? (
          <div>
            <div className="flex items-center gap-2">
              {steps.map((_, i) => (
                <span key={i} className={`h-[2px] flex-1 ${i <= step ? 'bg-gold' : 'bg-stone-warm'}`} />
              ))}
            </div>
            <p className="mt-6 eyebrow text-espresso/60">Question {step + 1} of {steps.length}</p>
            <h2 className="mt-2 font-display text-4xl md:text-5xl">{steps[step].q}</h2>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {steps[step].opts.map((o) => (
                <button key={String(o.v)} onClick={() => pick(steps[step].k, o.v)} className="group flex items-center justify-between border border-stone-warm bg-white px-5 py-5 text-left transition hover:border-gold hover:bg-ivory">
                  <span className="font-display text-xl">{o.l}</span>
                  <ArrowRight className="size-4 text-espresso/50 transition group-hover:translate-x-1 group-hover:text-gold" />
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <p className="eyebrow flex items-center gap-2 text-gold"><Gift className="size-4" /> Your edit</p>
            <h2 className="mt-2 font-display text-4xl md:text-5xl">A thoughtful match.</h2>
            <p className="mt-3 text-espresso/70">Based on your answers, these pieces feel right.</p>

            {results.bundles.length > 0 && (
              <div className="mt-10">
                <p className="eyebrow text-espresso/60">Suggested bundles</p>
                <div className="mt-4 grid gap-6 md:grid-cols-2">
                  {results.bundles.map((b) => (
                    <Link to="/bundles" key={b.id} className="group border border-stone-warm bg-white p-4 transition hover:border-gold">
                      <div className="aspect-[4/5] overflow-hidden bg-champagne"><img src={b.image} alt={b.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                      <h3 className="mt-4 font-display text-xl">{b.title}</h3>
                      <p className="mt-1 text-sm">R {b.price} · <span className="text-gold">Save R {b.saves}</span></p>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-12">
              <p className="eyebrow text-espresso/60">You may also love</p>
              {results.products.length === 0 ? (
                <p className="mt-4 text-espresso/70">Nothing matched exactly. Try adjusting your answers.</p>
              ) : (
                <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  {results.products.map((p) => (
                    <Link key={p.id} to={`/product/${p.id}`} className="group block">
                      <div className="aspect-[4/5] overflow-hidden bg-champagne"><img src={p.image} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                      <h3 className="mt-3 font-display text-lg">{p.title}</h3>
                      <p className="text-sm text-espresso/70">R {Number(p.price).toFixed(0)}</p>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <button onClick={reset} className="mt-10 inline-flex items-center gap-2 text-[11px] tracking-[0.18em] text-espresso hover:text-gold">
              <RotateCcw className="size-3" /> START AGAIN
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
